import { phoneDigits, phoneKey } from './customers';

export const PROSPECT_STATUSES = [
  { key: 'nuevo', label: 'Nuevo' },
  { key: 'contactado', label: 'Contactado' },
  { key: 'respondio', label: 'Respondió' },
  { key: 'interesado', label: 'Interesado' },
  { key: 'no_contactar', label: 'No contactar' }
];

export const WHATSAPP_STATES = [
  { key: 'unknown', label: 'Sin verificar' },
  { key: 'yes', label: 'Tiene WhatsApp' },
  { key: 'no', label: 'Sin WhatsApp' }
];

export const statusLabel = (key) => PROSPECT_STATUSES.find(s => s.key === key)?.label || 'Nuevo';

// Google devuelve teléfonos como "+57 300 123 4567", "(601) 234 5678" o "300 1234567".
// Solo un celular puede tener WhatsApp personal/Business: en Colombia son 10 dígitos que empiezan por 3.
// Los fijos (601, 604, …) se descartan sin gastar una verificación. Otros países: se normaliza y se deja
// que la verificación decida ('unknown').
export function classifyPhone(raw, country = '57') {
  let digits = phoneDigits(raw).replace(/^00/, '');
  if (digits.length < 7) return { digits: '', kind: 'invalid' };

  if (country !== '57') return { digits, kind: digits.length >= 8 ? 'unknown' : 'invalid' };

  if (digits.startsWith('57') && digits.length === 12) digits = digits.slice(2);
  if (digits.length !== 10) return { digits: '', kind: 'invalid' };
  if (digits.startsWith('3')) return { digits: `57${digits}`, kind: 'mobile' };
  return { digits: `57${digits}`, kind: 'landline' };
}

const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Claves (últimos 10 dígitos) de los clientes que ya tenemos: a esos no se les ofrece el servicio como prospectos.
export const customerKeySet = (customers) =>
  new Set((customers || []).map(c => phoneKey(c.phone)).filter(k => k.length >= 7));

export const isExistingCustomer = (prospect, keys) => Boolean(prospect.phone) && keys.has(phoneKey(prospect.phone));

// Un prospecto se puede contactar en la Fase 2 solo si tiene WhatsApp confirmado, está "nuevo"
// y no es ya cliente. "No contactar" es definitivo.
export const isContactable = (prospect, keys = new Set()) =>
  prospect.whatsapp === 'yes' && prospect.status === 'nuevo' && !isExistingCustomer(prospect, keys);

export function filterProspects(list, { text = '', status = 'all', whatsapp = 'all' } = {}) {
  const q = norm(text).trim();
  return (list || []).filter(p => {
    if (status !== 'all' && p.status !== status) return false;
    if (whatsapp !== 'all' && p.whatsapp !== whatsapp) return false;
    if (!q) return true;
    return [p.name, p.category, p.address, p.city, p.phone].some(v => norm(v).includes(q));
  });
}

export function prospectSummary(list, keys = new Set()) {
  const out = { total: 0, withWhatsapp: 0, toVerify: 0, contactable: 0, optedOut: 0 };
  (list || []).forEach(p => {
    out.total += 1;
    if (p.whatsapp === 'yes') out.withWhatsapp += 1;
    if (p.whatsapp === 'unknown' && p.phone) out.toVerify += 1;
    if (p.status === 'no_contactar') out.optedOut += 1;
    if (isContactable(p, keys)) out.contactable += 1;
  });
  return out;
}

// Validación del formulario de búsqueda (la base de datos también limita max_results a 1-60).
export function validateSearch({ query, city, maxResults }) {
  const q = String(query ?? '').trim();
  const c = String(city ?? '').trim();
  const max = Number(maxResults);
  if (q.length < 3) return { ok: false, error: 'Escribe el rubro que buscas (ej. barberías, clínicas dentales).' };
  if (c.length < 2) return { ok: false, error: 'Escribe la ciudad.' };
  if (!Number.isInteger(max) || max < 1 || max > 60) return { ok: false, error: 'El máximo debe estar entre 1 y 60.' };
  return { ok: true, value: { query: q, city: c, max_results: max } };
}

export const SEARCH_STATUS_LABEL = {
  pendiente: 'En cola',
  procesando: 'Buscando…',
  listo: 'Listo',
  error: 'Con error'
};

// Una búsqueda que lleva mucho en cola casi siempre significa que el conector de n8n no está activo o falta la clave de Google.
export const STALE_SEARCH_MINUTES = 10;
export const isStaleSearch = (search, now = new Date()) =>
  search.status === 'pendiente' && now - new Date(search.created_at) > STALE_SEARCH_MINUTES * 60000;

// Respuestas que llegaron con el identificador interno de WhatsApp (@lid) y sin teléfono: el bot no sabe a qué prospecto
// corresponden hasta que se vinculan (solas, por el nombre del perfil, o a mano desde aquí).
export const UNIDENTIFIED_DAYS = 7;

export function unidentifiedReplies(messages, customers, prospects) {
  const linked = new Set((prospects || []).map(p => p.lid).filter(Boolean));
  const names = new Map((customers || []).filter(c => c.lid).map(c => [String(c.lid), (c.name || '').trim()]));
  const byLid = new Map();
  (messages || []).forEach((m) => {
    const lid = m?.lid ? String(m.lid) : '';
    if (!lid || linked.has(lid)) return;
    const prev = byLid.get(lid);
    if (!prev || new Date(m.sent_at) > new Date(prev.sentAt)) {
      byLid.set(lid, { lid, name: names.get(lid) || '', lastMessage: String(m.body || ''), sentAt: m.sent_at });
    }
  });
  return [...byLid.values()].sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
}

// A quién se puede vincular una respuesta: prospectos ya contactados que todavía no tienen un @lid.
export const linkCandidates = (prospects) =>
  (prospects || []).filter(p => p.status !== 'nuevo' && !p.lid).sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));

